import React, { useCallback } from "react";
import { createContext, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { trackPageView, trackEvent, startSession } from "../analytics/sessionId";
import PropTypes from "prop-types";
import * as eventTemplates from "../analytics/events";

const SessionContext = createContext();

export function SessionTracker({ children }) {
  const { user, isLoading, sessionId } = useContext(AuthContext);
  const location = useLocation();
  const previousUserRef = useRef(null);
  const sessionStartedRef = useRef(false);

  useEffect(() => {
    if (isLoading || sessionStartedRef.current || user) return;

    // Only start session if user state has changed
    if (user?._id !== previousUserRef.current) {
      console.log(`Starting session for user: ${user?._id || "null"}`);
      startSession(user?._id || null);
      previousUserRef.current = user?._id || null;
      sessionStartedRef.current = true; // Ensure session has been started
    }
  }, [user, isLoading]);

  useEffect(() => {
      if (sessionId && !isLoading) {
        console.log("tracking page view...");
        const pageUrl = window.location.href;
        trackPageView(user?._id, pageUrl);
      }
  }, [location.pathname])


  // handle route changes for non-router navigation (refresh, back, forward, manual entry)
  useEffect(() => {
    const handleRouteChange = () => {
      if (user?._id) {
        // update page url when route changes
        const pageUrl = window.location.href;
        trackPageView(user._id, pageUrl);
      }
    };

    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, [user?._id]);

  // handle events
  const trackCustomEvent = useCallback((eventType, eventData = {}, additionalData = {}) => {
    console.log("additional data in trackcustomevent: ", additionalData);
    if (!user?._id) {
        console.warn("User ID not available, skipping event tracking");
        return;
      }
  
      // Get the event template if it exists
      const template = eventTemplates[eventType];
      if (!template) {
        console.error(`No template found for event type: ${eventType}`);
        return;
      }
  
      // Validate event data using the template
      try {
        template.validate(eventData);
      } catch (error) {
        console.error(`Validation failed for ${eventType}:`, error.message);
        return;
      }
  
      // Create event data using the template
      const finalEventData = template.createEventData(eventData, {
        ...additionalData
      });
  
      // Track the event with session and user context
      trackEvent(user._id, template.eventType, finalEventData);
    }, [user?._id]);

  // Provide trackCustomEvent function via context
  const contextValue = {
    trackEvent: trackCustomEvent,
  }

  return (
    <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>
  );
}

SessionTracker.propTypes = {
  children: PropTypes.node,
};

export const useSession = () => useContext(SessionContext);
